import { useCallback, useMemo } from 'react';

import { useUserShopCarts } from '#cart/useUserShopCarts.js';
import { GoToCheckoutButton } from '#components/molecules/Cart/buttons/GoToCheckoutButton/GoToCheckoutButton.js';
import { useShopStorage } from '#hooks/useShopStorage.js';
import { type ICause, useShowCauseW_Selector } from '@givebacks/api/core';
import {
  type Cart,
  type ConsumerCheckoutCartsResponse,
  useShowConsumerCheckoutFromCartIds,
} from '@givebacks/api/store';
import { centsToDollar } from 'core/Utils';
import { Card, Group, LoadingOverlay, Stack, Text } from 'ui';

type CartDetailsItemProps = {
  cart: Cart;
  storeId: string;
};

const CartsSubTotal = () => {
  const { carts } = useUserShopCarts();

  const cartIds = useMemo(
    () => carts?.map((cart) => cart?.uuid).filter((cartId) => cartId !== undefined),
    [carts]
  );

  const { data: subtotal } = useShowConsumerCheckoutFromCartIds({
    cartIds,
    select: useCallback((data: ConsumerCheckoutCartsResponse) => data?.checkout.subtotal ?? 0, []),
  });

  return (
    <Group justify="space-between">
      <Text fz="14" fw={600}>
        Cart Subtotal
      </Text>
      <Text fz="14" fw={600}>
        {centsToDollar(subtotal ?? 0)}
      </Text>
    </Group>
  );
};

const CartDetailsLineItem = ({ cart, storeId }: CartDetailsItemProps) => {
  const { data: causeName } = useShowCauseW_Selector({
    uuid: storeId,
    select: useCallback((data?: ICause) => data?.name, []),
  });

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Text fz="14" truncate="end">
        {causeName}
      </Text>
      <Text fz="14" fw={600}>
        {centsToDollar(cart.subtotal)}
      </Text>
    </Group>
  );
};

/** List of all carts with their details and Cart Subtotal for all carts */
const CartDetailsList = () => {
  const { carts, isLoading } = useUserShopCarts();
  const { getCartByCartId } = useShopStorage();

  if (isLoading) return <LoadingOverlay visible={isLoading} />;
  return (
    <Stack gap="4">
      {carts?.map((cart) => {
        if (!cart) return null;
        const storeId = getCartByCartId(cart.uuid);
        if (!storeId) return null;
        return <CartDetailsLineItem key={cart.uuid} cart={cart} storeId={storeId.storeId} />;
      })}
      <CartsSubTotal />
    </Stack>
  );
};

/** Renders details of all carts the user currently has visible in the cart page */
const CartsDetails = () => {
  return (
    <Card shadow="md" p="xl" w="425px">
      <Stack gap="lg">
        <Text fw={600} fz="18">
          Cart Details
        </Text>
        <CartDetailsList />
        <GoToCheckoutButton />
      </Stack>
    </Card>
  );
};

export { CartsDetails };
